import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatasourceType } from '@selfai-platform/pipeline-common';
import { AlertService } from '@selfai-platform/shared';
import {
  BreadcrumbsMenuItem,
  BreadcrumbsMenuService,
  KE_DATASOURCES_PATH,
  KE_ROOT_ROUTE,
} from '@selfai-platform/shell';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { catchError, combineLatest, take, throwError } from 'rxjs';
import { getDataSourceCreateRoute, getDataSourceEditRoute } from '../../../utils';
import { DataSource, DataSourcesAdditions } from '../../model';
import { DataSourceAdditionsDomainService, DataSourcePermissionService } from '../../services';
import { getVisibility } from '../../utilities';
import { DataSourcesDbComponent } from '../data-sources-db/data-sources-db.component';
import { DataSourcesFileComponent } from '../data-sources-file/data-sources-file.component';
import { DataSourcesGoogleSpreadsheetComponent } from '../data-sources-google-spreadsheet/data-sources-google-spreadsheet.component';
import { DataSourcesHdfsComponent } from '../data-sources-hdfs/data-sources-hdfs.component';
import { DataSourcesHiveComponent } from '../data-sources-hive/data-sources-hive.component';
import { DataSourcesKafkaComponent } from '../data-sources-kafka/data-sources-kafka.component';
import { DataSourcesLibraryComponent } from '../data-sources-library/data-sources-library.component';
import { DataSourcesS3Component } from '../data-sources-s3/data-sources-s3.component';
import { DataSourceComponentService } from './data-sources-component.service';
import { DataSourceFormService } from './data-sources-form.service';

@Component({
  selector: 'selfai-platform-data-sources',
  imports: [
    CommonModule,
    TranslateModule,
    ToolbarModule,
    ButtonModule,
    CardModule,
    DataSourcesDbComponent,
    DataSourcesFileComponent,
    DataSourcesGoogleSpreadsheetComponent,
    DataSourcesHdfsComponent,
    DataSourcesKafkaComponent,
    DataSourcesS3Component,
    DataSourcesLibraryComponent,
    DataSourcesHiveComponent,
    ToastModule,
  ],
  templateUrl: './data-sources.component.html',
  styleUrls: ['./data-sources.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DataSourceComponentService, DataSourceFormService],
})
export class DataSourcesComponent implements OnChanges, OnInit {
  public get templateType(): DatasourceType | undefined {
    return this.dataSource?.params?.datasourceType || this.datasourceType;
  }

  public id: string = this.activatedRoute?.snapshot?.params['id'];
  public editMode = !!this.id;
  public canUpdate = false;
  public form = this.dataSourceFormService.form;
  public breadcrumbItems: BreadcrumbsMenuItem[] = [];

  @Input() dataSource: DataSource;
  @Input() dataSourceList: DataSource[];
  @Input() datasourceType: DatasourceType;
  @Input() additionalList: DataSourcesAdditions[];

  @Output() backEvent = new EventEmitter<void>();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly translate: TranslateService,
    private readonly breadcrumbsMenuService: BreadcrumbsMenuService,
    private readonly dataSourceAdditionsDomainService: DataSourceAdditionsDomainService,
    private readonly dataSourceComponentService: DataSourceComponentService,
    private readonly dataSourceFormService: DataSourceFormService,
    private readonly datasourcesPermissionsService: DataSourcePermissionService,
    private readonly alertService: AlertService,
    private readonly router: Router,
  ) {}

  public ngOnInit(): void {
    if (!this.editMode) {
      this.setBreadcrumbForCreate();
    }
    this.dataSourceAdditionsDomainService.loadAdditionParams(this.templateType);
  }

  public ngOnChanges(): void {
    if (this.editMode && this.dataSource) {
      this.breadcrumbItems = [
        {
          label: this.translate.instant('shell.menu.data-sources'),
          routerLink: ['/', KE_ROOT_ROUTE, KE_DATASOURCES_PATH],
        },
        {
          label: `${this.translate.instant('shell.bc.data-sources.label.edit')} ${this.dataSource.params?.name}`,
          routerLink: getDataSourceEditRoute(this.dataSource.id),
        },
      ];

      this.breadcrumbsMenuService.setBreadcrumbsMenu(this.breadcrumbItems);
    }

    this.checkCanUpdate();
  }

  public onSubmit(): void {
    this.dataSourceComponentService
      .saveDataSource(this.dataSource)
      .pipe(
        take(1),
        catchError((e) => {
          this.alertService.error(this.translate.instant('shell.bc.data-sources.save-status.error'));

          return throwError(() => e);
        }),
      )
      .subscribe((dataSource) => {
        if (dataSource) {
          this.alertService.success(this.translate.instant('shell.bc.data-sources.save-status.success'));
          this.backEvent.emit();
        }
      });
  }

  public onBackEvent(): void {
    this.backEvent.emit();
  }

  private checkCanUpdate(): void {
    if (this.editMode) {
      combineLatest([
        this.datasourcesPermissionsService.canUpdateSource(
          this.dataSource?.['ownerId'] as string,
          getVisibility(this.dataSource.params.visibility) === 'public',
        ),
        this.datasourcesPermissionsService.canViewSource(
          this.dataSource?.['ownerId'] as string,
          getVisibility(this.dataSource?.params.visibility) === 'public',
        ),
      ]).subscribe(([canUpdate, canView]) => {
        if (!canView) {
          this.router.navigate(['access-denied']).then();
        }
        this.canUpdate = canUpdate;
        if (!canUpdate) {
          this.form.disable();
        }
      });
    }
  }

  private setBreadcrumbForCreate(): void {
    this.breadcrumbItems = [
      {
        label: this.translate.instant('shell.menu.data-sources'),
        routerLink: ['/', KE_ROOT_ROUTE, KE_DATASOURCES_PATH],
      },
      {
        label: `${this.translate.instant('shell.bc.data-sources.label.create')} "${this.templateTypeToLabel(this.templateType)}"`,
        routerLink: getDataSourceCreateRoute(this.templateType),
      },
    ];
    this.breadcrumbsMenuService.setBreadcrumbsMenu(this.breadcrumbItems);
  }

  private templateTypeToLabel(templateType: DatasourceType): string {
    switch (templateType) {
      case DatasourceType.JDBC:
        return this.translate.instant('data-source-list.toolbar.actions.create.database');
      case DatasourceType.EX_FILE:
        return this.translate.instant('data-source-list.toolbar.actions.create.external-file');
      case DatasourceType.GOOGLE_SP:
        return this.translate.instant('data-source-list.toolbar.actions.create.google-spreadsheet');
      case DatasourceType.HDFS:
        return this.translate.instant('data-source-list.toolbar.actions.create.hd');
      case DatasourceType.KAFKA:
        return this.translate.instant('data-source-list.toolbar.actions.create.kafka');
      case DatasourceType.S3:
        return this.translate.instant('data-source-list.toolbar.actions.create.s3');
      case DatasourceType.LIBRARY:
        return this.translate.instant('data-source-list.toolbar.actions.create.library');
      case DatasourceType.HIVE:
        return this.translate.instant('data-source-list.toolbar.actions.create.hive');
      default:
        return '';
    }
  }
}
